import { useState } from 'react';
import { customerContactsApi } from '../../../services/api';
import { CustomerContact } from '../../../types/customer-contact';
import { emptyContactForm } from '../../../features/customer-contacts/utils/contactHelpers';
import { showSuccess, showError } from '../../../utils/toast';

export const useContacts = () => {
  const [contacts, setContacts] = useState<CustomerContact[]>([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [editingContact, setEditingContact] = useState<CustomerContact | null>(null);
  const [contactForm, setContactForm] = useState({ ...emptyContactForm });
  const [submittingContact, setSubmittingContact] = useState(false);

  const loadContacts = async (customerId: number) => {
    try {
      setLoadingContacts(true);
      const response = await customerContactsApi.getAll({ customer_id: customerId });
      setContacts(response.data as unknown as CustomerContact[]);
    } catch (e: any) {
      console.error('Failed to load contacts:', e);
    } finally {
      setLoadingContacts(false);
    }
  };

  const resetContactState = () => {
    setShowContactForm(false);
    setEditingContact(null);
    setContactForm({ ...emptyContactForm });
  };

  const handleContactFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setContactForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleSubmitContact = async (
    e: React.FormEvent,
    selectedCustomerId: number,
  ) => {
    e.preventDefault();
    if (submittingContact) return;
    setSubmittingContact(true);
    try {
      if (editingContact) {
        await customerContactsApi.update(editingContact.id, {
          ...contactForm,
          customer_id: selectedCustomerId,
        });
        showSuccess('Contact updated successfully');
      } else {
        await customerContactsApi.create({
          ...contactForm,
          customer_id: selectedCustomerId,
        });
        showSuccess('Contact created successfully');
      }
      await loadContacts(selectedCustomerId);
      setShowContactForm(false);
      setEditingContact(null);
      setContactForm({ ...emptyContactForm });
    } catch (e: any) {
      showError(e?.response?.data?.message || e?.message || 'Failed to save contact');
    } finally {
      setSubmittingContact(false);
    }
  };

  const handleEditContact = (contact: CustomerContact) => {
    setEditingContact(contact);
    setContactForm({
      name: contact.name,
      email: contact.email || '',
      phone: contact.phone || '',
      title: contact.title || '',
      street: contact.street || '',
      city: contact.city || '',
      state: contact.state || '',
      zip: contact.zip || '',
      country: contact.country || '',
      notes: contact.notes || '',
      is_primary: contact.is_primary,
    });
    setShowContactForm(true);
  };

  const handleDeleteContact = async (contactId: number, selectedCustomerId: number) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;
    try {
      await customerContactsApi.delete(contactId);
      await loadContacts(selectedCustomerId);
      showSuccess('Contact deleted successfully');
    } catch (e: any) {
      showError(e?.response?.data?.message || e?.message || 'Failed to delete contact');
    }
  };

  return {
    contacts,
    loadingContacts,
    showContactForm,
    setShowContactForm,
    editingContact,
    setEditingContact,
    contactForm,
    setContactForm,
    submittingContact,
    loadContacts,
    resetContactState,
    handleContactFormChange,
    handleSubmitContact,
    handleEditContact,
    handleDeleteContact,
  };
};
